import React, { useState, useEffect, useRef } from 'react';
// import './SaveProfileModal.css'; // Disabled: Using Tailwind classes instead to avoid global style conflicts

interface SaveProfileModalProps {
  onSave: (profileName: string) => Promise<void>;
  onClose: () => void;
  initialName?: string;
  mode: 'create' | 'edit';
  formName?: string;
  existingProfileNames?: string[];
}

/**
 * SaveProfileModal Component
 *
 * Modal dialog for saving or editing user profiles
 * Enhanced with form context, duplicate validation, and loading state
 */
const SaveProfileModal: React.FC<SaveProfileModalProps> = ({
  onSave,
  onClose,
  initialName = '',
  mode = 'create',
  formName,
  existingProfileNames = []
}) => {
  const [profileName, setProfileName] = useState(initialName);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Focus input field on mount
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);
  
  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);
  
  // Handle keyboard events for accessibility
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      } else if (event.key === 'Enter') {
        handleSave();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, profileName]);
  
  // Handle profile name input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setProfileName(value);

    // Clear error when user types
    if (error) setError(null);

    // Real-time validation for duplicate names (case-insensitive)
    if (value.trim() && mode === 'create') {
      const isDuplicate = existingProfileNames.some(
        (name) => name.toLowerCase() === value.trim().toLowerCase()
      );
      if (isDuplicate) {
        setError('A profile with this name already exists');
      }
    }
  };

  // Validate profile name
  const validateProfileName = (name: string): string | null => {
    const trimmedName = name.trim();

    if (!trimmedName) {
      return 'Profile name is required';
    }

    if (trimmedName.length > 50) {
      return 'Profile name must be 50 characters or less';
    }

    const nameRegex = /^[a-zA-Z0-9\s\-_]+$/;
    if (!nameRegex.test(trimmedName)) {
      return 'Profile name can only contain letters, numbers, spaces, hyphens, and underscores';
    }

    // Check for duplicate (case-insensitive), but allow same name when editing
    if (mode === 'create') {
      const isDuplicate = existingProfileNames.some(
        (existingName) => existingName.toLowerCase() === trimmedName.toLowerCase()
      );
      if (isDuplicate) {
        return 'A profile with this name already exists';
      }
    }

    return null;
  };

  // Validate and save profile
  const handleSave = async () => {
    const validationError = validateProfileName(profileName);
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await onSave(profileName.trim());
      // Modal will be closed by parent component on success
    } catch (err: any) {
      // Show error in modal (allow retry)
      setError(err.message || 'Failed to save profile. Please try again.');
      setIsLoading(false);
    }
  };
  
  const characterCount = profileName.length;
  const maxLength = 50;

  return (
    <div className="fixed top-0 left-0 right-0 bottom-0 bg-black/50 flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-[450px] animate-fadeIn overflow-hidden" ref={modalRef}>
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
          <h3 className="m-0 text-lg font-semibold text-gray-700">{mode === 'create' ? 'Save New Profile' : 'Edit Profile'}</h3>
          <button
            className="text-gray-400 hover:text-[#1E3B8C] hover:bg-blue-50 rounded-full w-7 h-7 flex items-center justify-center transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#1E3B8C] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed text-2xl leading-none"
            onClick={onClose}
            disabled={isLoading}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        <div className="p-6">
          {/* Form context display */}
          {formName && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <span className="font-medium">Form:</span> {formName}
              </p>
            </div>
          )}

          <div className="mb-2">
            <label htmlFor="profile-name" className="block text-sm font-medium text-gray-600 mb-2">Profile Name</label>
            <input
              ref={inputRef}
              id="profile-name"
              type="text"
              value={profileName}
              onChange={handleChange}
              placeholder="Enter a name for this profile"
              maxLength={maxLength}
              disabled={isLoading}
              aria-invalid={!!error}
              aria-describedby={error ? 'profile-name-error' : 'profile-name-counter'}
              className="w-full px-3 py-3 border border-gray-200 rounded text-[15px] transition-all duration-200 focus:outline-none focus:border-blue-300 focus:shadow-[0_0_0_3px_rgba(66,153,225,0.2)]"
            />

            {/* Character counter */}
            <div className="flex justify-between items-center mt-1">
              <div id="profile-name-counter" className="text-xs text-gray-500">
                {characterCount}/{maxLength} characters
              </div>
              {error && (
                <div id="profile-name-error" className="text-red-600 text-[13px] mt-2" role="alert">
                  {error}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end px-6 py-4 bg-gray-50 border-t border-gray-200 gap-3">
          <button
            className="bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 hover:border-gray-400 px-5 py-2.5 rounded font-medium text-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            className="bg-[#1E3B8C] hover:bg-[#152d6b] text-white px-5 py-2.5 rounded font-medium text-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#1E3B8C] focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center"
            onClick={handleSave}
            disabled={!profileName.trim() || !!error || isLoading}
          >
            {isLoading ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Saving...
              </>
            ) : mode === 'create' ? (
              'Save Profile'
            ) : (
              'Update Profile'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SaveProfileModal; 